import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { environment } from '../../../../../environments/environment';
import { ParentButtonComponent } from '../../../../shared/components/ui/parent-button/parent-button.component';
import { IdentityConsentCardComponent } from '../../components/identity-consent-card/identity-consent-card.component';
import {
  IdentityVerificationTier,
  KYC_PLACEHOLDER_PROVIDER,
  KycMockStep,
  IDENTITY_CONSENT_TERMS_VERSION,
} from '../../models/identity-verification.model';
import { ParentIdentityService } from '../../services/parent-identity.service';
import { StepUpPasswordService } from '../../services/step-up-password.service';
import { GaEventService } from '../../../../core/services/ga.service';
import {
  PARENT_ANALYTICS_LAYOUT,
  ParentAnalyticsEvents,
} from '../../../../core/analytics/parent-analytics.events';

@Component({
  selector: 'app-parent-identity-verify',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    ParentButtonComponent,
    IdentityConsentCardComponent,
  ],
  templateUrl: './parent-identity-verify.component.html',
  styleUrl: './parent-identity-verify.component.scss',
})
export class ParentIdentityVerifyComponent implements OnInit {
  #router = inject(Router);
  #identity = inject(ParentIdentityService);
  #stepUp = inject(StepUpPasswordService);
  #analytics = inject(GaEventService);

  readonly step = signal<KycMockStep>('intro');
  readonly documentType = signal<'passport' | 'id_card'>('passport');
  readonly consentAccepted = signal(false);
  readonly isBusy = signal(false);
  readonly simulateFailed = signal(false);
  readonly identity = this.#identity;
  readonly isVerified = computed(() => this.#identity.isDocumentVerified());
  readonly session = computed(() => this.#identity.session());
  readonly showSimulate = computed(
    () =>
      !environment.production &&
      (this.session()?.provider === KYC_PLACEHOLDER_PROVIDER ||
        !this.session()?.provider),
  );

  async ngOnInit(): Promise<void> {
    await this.#identity.whenConfigReady();
    if (!this.#identity.isLayerEnabled()) {
      await this.#router.navigate(['/home']);
      return;
    }

    await this.#identity.reload();
    if (this.#identity.isDocumentVerified()) {
      await this.#router.navigate(['/home']);
      return;
    }

    await this.#identity.loadSession();
    if (this.#identity.session()) {
      this.step.set('consent');
    }
  }

  async startVerification(): Promise<void> {
    this.step.set('consent');
  }

  async goToDocumentType(): Promise<void> {
    if (!this.consentAccepted() || this.isBusy()) {
      return;
    }

    const stepUp = await this.#stepUp.prompt({
      titleKey: 'app.identity.stepUp.kycTitle',
      bodyKey: 'app.identity.stepUp.kycBody',
      confirmKey: 'app.identity.stepUp.confirm',
    });
    if (!stepUp) {
      return;
    }

    this.isBusy.set(true);
    try {
      await this.#identity.createSession({
        consentAccepted: true,
        termsVersion: IDENTITY_CONSENT_TERMS_VERSION,
        password: stepUp.password,
        totpCode: stepUp.totpCode,
      });
      this.step.set('documentType');
    } finally {
      this.isBusy.set(false);
    }
  }

  selectDocumentType(type: 'passport' | 'id_card'): void {
    this.documentType.set(type);
    this.step.set('documentFront');
  }

  nextFromDocumentFront(): void {
    this.step.set('documentBack');
  }

  nextFromDocumentBack(): void {
    this.step.set('selfie');
  }

  async completeCapture(): Promise<void> {
    if (this.isBusy()) return;
    this.step.set('processing');
    this.isBusy.set(true);

    await new Promise((resolve) => setTimeout(resolve, 1800));

    try {
      if (this.showSimulate()) {
        const status = await this.#identity.simulateVerification({
          success: !this.simulateFailed(),
          documentCountry: 'NL',
        });
        this.simulateFailed.set(false);
        const verified =
          status.tier === IdentityVerificationTier.DOCUMENT_VERIFIED;
        if (verified) {
          this.#analytics.sendEvent(ParentAnalyticsEvents.VERIFY_IDENTITY, {
            layout: PARENT_ANALYTICS_LAYOUT,
            method: 'simulate',
            success: true,
          });
        }
        this.step.set('result');
        return;
      }

      await this.#identity.reload();
      if (this.#identity.isDocumentVerified()) {
        this.#analytics.sendEvent(ParentAnalyticsEvents.VERIFY_IDENTITY, {
          layout: PARENT_ANALYTICS_LAYOUT,
          method: 'document',
          success: true,
        });
      }
      this.step.set('result');
    } finally {
      this.isBusy.set(false);
    }
  }

  async finish(): Promise<void> {
    await this.#router.navigate(['/home']);
  }

  skipForNow(): void {
    void this.#router.navigate(['/home']);
  }

  goBack(): void {
    const order: KycMockStep[] = [
      'intro',
      'consent',
      'documentType',
      'documentFront',
      'documentBack',
      'selfie',
    ];
    const idx = order.indexOf(this.step());
    if (idx > 0) {
      this.step.set(order[idx - 1]);
    }
  }
}
